import React, { FC, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Input } from '@.ui';
import { StoreClusterModel } from '@/Components/Pages/ServiceProviderPage/components';
import { TabButton } from '@/Components/UI/TabButton';
import { LinkHelper } from '@/Services';

import { StoreClusterMode, storeClusterStoreSelectors } from '../../../model/StoreClusterStore';
import S from './StoreClusterFormHeader.module.scss';

interface StoreClusterFormHeaderProps {
    title: ReactNode;

    onSave: (cluster: StoreClusterModel) => void;
}

export const StoreClusterFormHeader: FC<StoreClusterFormHeaderProps> = ({ title, ...props }) => {
    const navigate = useNavigate();

    const isAllowToSaveStoreCluster = storeClusterStoreSelectors.use.isAllowToSaveStoreCluster();

    const storeClusterMode = storeClusterStoreSelectors.use.mode();
    const changeStoreClusterMode = storeClusterStoreSelectors.use.changeStoreClusterMode();

    const setStoreClusterName = storeClusterStoreSelectors.use.setStoreClusterName();
    const storeClusterName = storeClusterStoreSelectors.use.storeClusterName();

    const getStoreClusterModel = storeClusterStoreSelectors.use.getStoreClusterModel();

    const toStoreClusterList = () => navigate(LinkHelper.serviceProvider.storeClusterList);

    const storeClusterModeTabButtons = useMemo(
        () => [
            {
                title: 'Companies',
                mode: StoreClusterMode.COMPANIES,
            },
            {
                title: 'Chain',
                mode: StoreClusterMode.STORE_GROUPS,
            },
            {
                title: 'Stores',
                mode: StoreClusterMode.STORES,
            },
        ],
        []
    );

    const isStoreClusterTabButtonActive = (mode: StoreClusterMode): boolean => {
        return mode === storeClusterMode;
    };

    const createChangeStoreClusterMode = (nextMode: StoreClusterMode) => () => {
        changeStoreClusterMode(nextMode);
    };

    const onSave = () => {
        props.onSave(getStoreClusterModel());
    };

    return (
        <div className={S.root}>
            <h1 className={S.root__row}>
                {title}

                <div className={S.buttons}>
                    <Button color="transparent" onClick={toStoreClusterList}>
                        Cancel
                    </Button>
                    <Button color="brown" disabled={!isAllowToSaveStoreCluster()} onClick={onSave}>
                        Save
                    </Button>
                </div>
            </h1>

            <div className={S.root__row}>
                <Input
                    className={S.storeClusterNameInput}
                    text="Store cluster name"
                    placeholder="Name of cluster"
                    required
                    value={storeClusterName}
                    onChange={setStoreClusterName}
                />

                <div className={S.buttons}>
                    {storeClusterModeTabButtons.map((button) => (
                        <TabButton key={button.mode} isActive={isStoreClusterTabButtonActive(button.mode)} onClick={createChangeStoreClusterMode(button.mode)}>
                            {button.title}
                        </TabButton>
                    ))}
                </div>
            </div>
        </div>
    );
};
